import "./style.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Row } from "@amzn/stencil-react-components/layout";
import IconMessageHelpLarge from "@amzn/stencil-react-icons/icons/icon-message-help-large";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { EmbeddedMetric, Logger } from "@/apis/Logger";
import aivaIcon from "@/assets/images/aivaIcon.svg";
import { Chatbot } from "@/components/Chatbot";
import { ChatHeader } from "@/components/Chatbot/ChatHeader";
import { ChatWidget } from "@/components/GetSupport";
import { CHAT_SUPPORT_CONTEXTS_METRICS_MAP } from "@/components/GetSupport/ChatWithITSupport/constants";
import { SelfHelpFAQContainer } from "@/components/GetSupport/SelfHelpFAQ/SelfHelpFAQContainer";
import { isSelfHelpEnabled } from "@/helpers/faq-helper";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { useFeatureFlag } from "@/stores/contexts/FeatureFlagsContext";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

import VerifyWithItFlyoutContent from "../VerifyWithItFlyoutContent/VerifyWithItFlyoutContent";

/**
 * Implements the chat support flyout panel element.
 */
export const ChatSupportFlyout = () => {
  const { dispatch, state } = useContext(AppContext);
  const intl = useIntl();

  /**
   * Local state controls rendering (ChatWidget mount timing).
   * Global state.chatInitialized is synced for GetSupportHeaderLink to read.
   * Both are needed: local state preserves the exact render timing that
   * ChatWidget's useMemo depends on for capturing the correct refPage.
   */
  const [initialized, setInitialized] = useState(false);
  const isChatbotEnabled = useFeatureFlag("aiChatbot");
  const [activeTab, setActiveTab] = useState<"chatbot" | "support">(
    isChatbotEnabled ? "chatbot" : "support"
  );
  const [escalationSummary, setEscalationSummary] = useState("");

  useEffect(() => {
    // It takes a second for this state to load from the backend, so we need to reinitalize the activeTab state
    if (isChatbotEnabled) {
      setActiveTab("chatbot");
    }
  }, [isChatbotEnabled]);

  const isSelfHelpEnabledForStep = isSelfHelpEnabled(state.currentStep?.id);
  const showStartChatButton =
    !initialized &&
    !isSelfHelpEnabledForStep &&
    !state.getIdvSupportFlyoutVisible;
  const showIdvFlyoutContent = !initialized && state.getIdvSupportFlyoutVisible;

  const setChatInitializedHandler = () => {
    if (!initialized) {
      setInitialized(true);
      dispatch({ type: ActionType.SET_CHAT_INITIALIZED });

      Logger.publishWarnMetric(
        new EmbeddedMetric("InitializedChatWithSupport", "Count", 1, {
          RefPage: state.chatSupportRefPage,
        }),
        "New hire initiated chat with support",
        undefined,
        {
          amazonUsername: state.amazonUsername,
          region: state.activeRegion,
          refPage: state.chatSupportRefPage,
        }
      );

      if (state.nonDirectedChatSupportContext) {
        const { category, action } =
          CHAT_SUPPORT_CONTEXTS_METRICS_MAP[
            state.nonDirectedChatSupportContext
          ];
        trackStructEvent({
          category,
          action,
          label: Day1EventLabel.StartSupportChat,
        });
      } else {
        trackStructEvent({
          category: Day1EventCategory.PageLevel,
          action: Day1EventAction.ChatWithSupport,
          label: Day1EventLabel.StartSupportChat,
        });
      }
    } else {
      void Logger.info(`Chat already initialized, just showing existing chat`);
    }
  };

  /**
   * Sets the visibility of the chat support flyout on click.
   * @param isVisible Should the panel be visible?
   */
  const setVisibilityHandler = (isVisible: boolean) => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_FLYOUT_VISIBLE,
      chatSupportFlyoutVisible: isVisible,
    });

    dispatch({
      type: ActionType.SET_GET_IDV_SUPPORT_FLYOUT_VISIBLE,
      getIdvSupportFlyoutVisible: false,
    });

    if (!isSelfHelpEnabledForStep) {
      trackStructEvent({
        category: Day1EventCategory.PageLevel,
        action: Day1EventAction.ChatWithSupport,
        label: isVisible
          ? Day1EventLabel.OpenChatSupportPanel
          : Day1EventLabel.CloseChatSupportPanel,
      });
    }
  };

  /**
   * Calculates a class name for the panel contents depending on if the Get Support panel is open or not.
   * The GetSupport panel is a Stencil Flyout component which has a drop shadow by default, however the ChatSupportFlyout component is custom
   * and does not come out of the box with a drop shadow. If the GetSupport panel is open, it will cause a double shadow which is not a consistent experience.
   */
  const getFlyoutContentsClassNameString = () => {
    let classNameString = "custom-flyout-contents ";
    // visible if the panel is opened.
    if (state.chatSupportFlyoutVisible) {
      classNameString += "custom-flyout-contents-visible ";
    }
    // If the chat panel was opened without the get support panel open, it's missing a drop shadow that should be there.
    if (!state.getSupportFlyoutVisible) {
      classNameString += "custom-flyout-contents-visible-drop-shadow ";
    }
    return classNameString;
  };

  const FlyoutContents = () => {
    return (
      <>
        {isChatbotEnabled && (
          <div
            style={{ display: activeTab === "chatbot" ? "contents" : "none" }}
          >
            <Chatbot
              onContactSupport={(summary) => {
                setEscalationSummary(summary);
                setChatInitializedHandler();
                setActiveTab("support");
              }}
            />
          </div>
        )}
        <div style={{ display: activeTab === "support" ? "contents" : "none" }}>
          {initialized ? (
            <ChatWidget
              shouldInitialize={initialized}
              setVisibilityHandler={setVisibilityHandler}
              nonDirectedSupportCtx={state.nonDirectedChatSupportContext}
              refPage={state.chatSupportRefPage}
              refLink={state.chatSupportRefLink}
              username={state.amazonUsername}
              escalationSummary={escalationSummary}
              onDisconnect={() => setActiveTab("chatbot")}
            />
          ) : isSelfHelpEnabledForStep ? (
            <SelfHelpFAQContainer
              data-test-id={"selfHelpFAQContainer"}
              onChatClick={setChatInitializedHandler}
            />
          ) : showIdvFlyoutContent ? (
            <VerifyWithItFlyoutContent
              chatInitializedHandler={setChatInitializedHandler}
            />
          ) : showStartChatButton ? (
            <Row justifyContent="center" padding="S300">
              <Button
                variant={ButtonVariant.Secondary}
                onClick={setChatInitializedHandler}
                dataTestId="chatClickToStart"
                aria-label={intl.formatMessage({
                  id: "arialabels-clickToStartChat",
                })}
              >
                <FormattedMessage id="getSupport-chatSupport-clickToStart" />
              </Button>
            </Row>
          ) : null}
        </div>
      </>
    );
  };

  const getFlyoutTitle = () => {
    if (activeTab === "chatbot") {
      return "chatbot-header-title";
    }

    if (initialized) {
      return "text-chatWithITSupport";
    }

    if (isSelfHelpEnabledForStep) {
      return "getHelp-header";
    }
    return state.getIdvSupportFlyoutVisible
      ? "getSupport-header"
      : "text-chatWithITSupport";
  };

  return (
    <div className="custom-flyout-container">
      {/* This is the invisible left side of the page and provides an on-click to hide the flyout when anywhere else but the flyout contents is clicked. */}
      <div
        className={`custom-flyout-container-left ${
          state.chatSupportFlyoutVisible
            ? "custom-flyout-container-left-visible"
            : ""
        }`}
        onClick={() =>
          // Only close overlay if it's already open.
          state.chatSupportFlyoutVisible && setVisibilityHandler(false)
        }
        role="button"
        tabIndex={0}
        onKeyDown={() => {
          return;
        }}
        data-test-id="chatFlyoutLeftContainer"
        aria-hidden={!state.chatSupportFlyoutVisible}
        aria-label={intl.formatMessage({ id: "arialabels-clickToCloseChat" })}
      />
      <div className={getFlyoutContentsClassNameString()}>
        <ChatHeader
          title={intl.formatMessage({ id: getFlyoutTitle() })}
          subtitle={
            activeTab === "chatbot"
              ? intl.formatMessage({ id: "chatbot-header-subtitle" })
              : undefined
          }
          logo={
            activeTab === "support" ? (
              <IconMessageHelpLarge width={40} height={40} color="white" />
            ) : (
              <img src={aivaIcon} alt="" width={40} height={40} />
            )
          }
          color={
            activeTab === "chatbot" ? "color.purple.60" : "rgb(35, 47, 62)"
          }
          altLogo={
            isChatbotEnabled && initialized ? (
              activeTab === "chatbot" ? (
                <IconMessageHelpLarge width={28} height={28} color="white" />
              ) : (
                <img src={aivaIcon} alt="" width={28} height={28} />
              )
            ) : undefined
          }
          altColor={
            activeTab === "chatbot" ? "rgb(35, 47, 62)" : "rgb(112, 55, 163)"
          }
          onAltClick={() => {
            const nextTab = activeTab === "chatbot" ? "support" : "chatbot";
            if (nextTab === "chatbot") {
              trackStructEvent({
                category: Day1EventCategory.Chatbot,
                action: Day1EventAction.ChatbotOpened,
                property: "tab",
              });
            }
            setActiveTab(nextTab);
          }}
          onClose={() => setVisibilityHandler(false)}
        />
        <Row height="calc(100% - 72px)">
          <div className={`content-container`}>{FlyoutContents()}</div>
        </Row>
      </div>
    </div>
  );
};
